import type { Metadata } from 'next';
import { getTranslations } from 'next-intl/server';

import { execute, GetRouteDocument } from '@/fo/api/magento';
import { defaultStoreConfig } from '@/fo/feature/app/layout/base';
import { utilDecodeHtmlEntities } from '@/fo/util/decode-html-entities';

/** Имя query-параметра номера страницы (блог, специалисты и т.д.) */
export const LISTING_PAGE_PARAM = 'p';

/** Парсинг номера страницы из `p` */
export function parseListingPageParam(
  searchParams: Record<string, string | string[] | undefined> | undefined
): number {
  const raw = searchParams?.[LISTING_PAGE_PARAM];
  const value = Array.isArray(raw) ? raw[0] : raw;
  const page = Number(value ?? 1);
  return Number.isInteger(page) && page > 0 ? page : 1;
}

/** Канонический путь: без query на первой странице, `?p=N` при N > 1 */
export function buildListingCanonicalPath(
  pathname: string,
  page: number
): string {
  const path = pathname.toLowerCase().replace(/\/?$/, '/');
  const n = Number.isFinite(page) && page >= 1 ? page : 1;
  return n > 1 ? `${path}?${LISTING_PAGE_PARAM}=${n}` : path;
}

/** Общая генерация metadata для пагинированных листингов */
export async function generateListingMetadata(options: {
  pathname: string;
  page: number;
}): Promise<Metadata> {
  const { pathname, page } = options;
  const { data } = await execute(GetRouteDocument, { url: pathname });
  const seo = data?.seoParams || {};

  let title = utilDecodeHtmlEntities(
    seo.meta_title || defaultStoreConfig.storeConfig.default_title || ''
  );
  let description = utilDecodeHtmlEntities(seo.meta_description || '');

  if (page > 1) {
    const t = await getTranslations('article.page.blog');
    const suffix = t('listingPagination.seoPageSuffix', { page });
    title = title ? `${title}${suffix}` : suffix.trim();
    description = description ? `${description}${suffix}` : '';
  }

  return {
    title,
    description,
    alternates: {
      canonical: buildListingCanonicalPath(pathname, page)
    }
  };
}
